import React, { useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import HomePage from "./pages/HomePage";
import TrackPage from "./pages/TrackPage";
import HistoryPage from "./pages/HistoryPage";
import JobSearchPage from "./pages/JobSearchPage";
import ProfilePage from "./pages/ProfilePage";
import RegisterPage from "./pages/RegisterPage";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import CustomerTrackingPage from "./pages/CustomerTrackingPage";
import ZonePreviewPage from "./pages/ZonePreviewPage";
import ClientApproval from "./pages/ClientApproval";
import ClientReportView from "./pages/ClientReportView";
import ClientLoginPage from "./pages/ClientLoginPage";
import ClientPortal from "./pages/ClientPortal";
import LeadIntakePage from "./pages/LeadIntakePage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import BottomNav from "./components/BottomNav";
import OfflineSyncBanner from "./components/OfflineSyncBanner";
import { Toaster } from "./components/ui/sonner";

// Protected route for authenticated users
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, userType } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-2"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect staff (admin, manager, auditor) to admin dashboard
  if (['admin', 'manager', 'auditor'].includes(userType)) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// Main app content with bottom nav - requires authentication
const MainApp = () => {
  const { isAuthenticated, loading, userType, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-2"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect staff (admin, manager, auditor) to admin dashboard
  if (['admin', 'manager', 'auditor'].includes(userType)) {
    return <Navigate to="/admin" replace />;
  }
  
  // Show pending message if user is not approved
  if (user?.status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center max-w-md">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⏳</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Account Pending</h2>
          <p className="text-gray-500 mb-4">
            Your account is pending approval. You'll receive access once an administrator reviews your application.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="app-container">
      <div className="app-content">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/track" element={<TrackPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/jobs" element={<JobSearchPage />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Routes>
      </div>
      <OfflineSyncBanner />
      <BottomNav />
    </div>
  );
};

// Redirect authenticated users away from login/register
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, userType } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-2"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  // If already logged in, redirect to appropriate page
  if (isAuthenticated) {
    if (['admin', 'manager', 'auditor'].includes(userType)) {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <Routes>
              {/* Public routes - redirect to home if already logged in */}
              <Route path="/register" element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              } />
              <Route path="/login" element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              } />
              <Route path="/admin/login" element={<LoginPage />} />
              
              {/* Public customer tracking page - no auth required */}
              <Route path="/track/:jobId" element={<CustomerTrackingPage />} />
              
              {/* Public zone preview page - no auth required */}
              <Route path="/zone-preview/:previewId" element={<ZonePreviewPage />} />
              
              {/* Public client approval page - no auth required */}
              <Route path="/client/approve/:token" element={<ClientApproval />} />

              {/* Public Report Page */}
              <Route path="/report/:token" element={<ClientReportView />} />

              {/* Public Lead-Intake page (polygon-draw) — no auth */}
              <Route path="/intake/:token" element={<LeadIntakePage />} />

              {/* Stripe post-checkout landings — must be PUBLIC (no auth) */}
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/payment-cancel" element={<PaymentCancelPage />} />

              {/* Client Portal */}
              <Route path="/client/login" element={<ClientLoginPage />} />
              <Route path="/client/*" element={<ClientPortal />} />
              
              {/* Admin routes */}
              <Route path="/admin/*" element={<AdminDashboard />} />
              
              {/* Protected app routes - require authentication */}
              <Route path="/*" element={<MainApp />} />
            </Routes>
            <Toaster position="top-center" />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
